// Тонкий клиент API. Auto-refresh JWT при 401 (Signature has expired).

const BASE = "/api/v1";
const STORAGE_KEY = "strangcalc.auth";

function loadAuth(): { token: string; refreshToken: string; email: string } | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
function saveAuth(token: string, refreshToken: string, email: string) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, refreshToken, email }));
}
function clearAuth() {
  localStorage.removeItem(STORAGE_KEY);
}

async function tryRefresh(): Promise<boolean> {
  const auth = loadAuth();
  if (!auth?.refreshToken) return false;
  try {
    const r = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: auth.refreshToken }),
    });
    if (!r.ok) {
      clearAuth();
      return false;
    }
    const t = await r.json();
    saveAuth(t.access_token, t.refresh_token, auth.email);
    return true;
  } catch {
    return false;
  }
}

async function doFetch(path: string, init?: RequestInit): Promise<Response> {
  const auth = loadAuth();
  const headers = new Headers(init?.headers);
  headers.set("Content-Type", "application/json");
  if (auth?.token) headers.set("Authorization", `Bearer ${auth.token}`);
  return fetch(`${BASE}${path}`, { ...init, headers });
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res = await doFetch(path, init);
  // 401 при наличии токена → пробуем refresh и повторяем 1 раз
  if (res.status === 401 && loadAuth()?.token) {
    const ok = await tryRefresh();
    if (ok) res = await doFetch(path, init);
    else {
      clearAuth();
      // Принудительно reload — AuthProvider увидит пустой localStorage и редирект на /login
      if (typeof window !== "undefined") window.location.reload();
    }
  }
  if (!res.ok) {
    let detail: any = null;
    try { detail = await res.json(); } catch { detail = await res.text(); }
    const msg = typeof detail === "string" ? detail : detail?.detail || res.statusText;
    throw new ApiError(res.status, msg, detail);
  }
  if (res.status === 204) return null as T;
  return res.json();
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail: any) {
    super(message);
  }
}

// ---- Auth ----
export async function register(email: string, password: string) {
  return request<{ access_token: string; refresh_token: string }>("/auth/register", {
    method: "POST", body: JSON.stringify({ email, password }),
  });
}
export async function login(email: string, password: string) {
  return request<{ access_token: string; refresh_token: string }>("/auth/login", {
    method: "POST", body: JSON.stringify({ email, password }),
  });
}
export async function me() {
  return request<{ id: string; email: string; naming_profile: string; base_currency: string }>("/auth/me");
}

// ---- Portfolios ----
export interface Portfolio {
  id: string;
  name: string;
  base_currency: string;
  status: string;
  instruments: any[];
  created_at: string;
  updated_at: string;
}

export async function listPortfolios() {
  return request<Portfolio[]>("/portfolios");
}
export async function getPortfolio(id: string) {
  return request<Portfolio>(`/portfolios/${id}`);
}
export async function createPortfolio(body: { name: string; instruments: any[] }) {
  return request<Portfolio>("/portfolios", { method: "POST", body: JSON.stringify(body) });
}
export async function updatePortfolio(id: string, body: Partial<{ name: string; instruments: any[]; status: string }>) {
  return request<Portfolio>(`/portfolios/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export async function deletePortfolio(id: string) {
  return request<null>(`/portfolios/${id}`, { method: "DELETE" });
}

// ---- Evaluate ----
export interface EvaluateBody {
  valuation_date: string;
  exit_date?: string | null;
  market: { spot: Record<string, number>; iv: Record<string, number>; risk_free_rate: number };
  portfolio_capital: number;
  iv_rank_pct?: number | null;
  horizon_days?: number | null;
  premium_cap_pct?: number;
  expected_realized_vol_pct?: number | null;
}
export interface EvaluateResponse {
  evaluation_id: string;
  accepted: boolean;
  gates: Record<string, { passed: boolean; rationale: string; evidence: any; skipped?: boolean }>;
  rejection_reasons: string[];
  valuation_date?: string;
  horizon_days?: number;
  exit_date?: string | null;
}
export async function evaluatePortfolio(id: string, body: EvaluateBody) {
  return request<EvaluateResponse>(`/portfolios/${id}/evaluate`, {
    method: "POST", body: JSON.stringify(body),
  });
}

// ---- Payoff ----
export interface PayoffBody {
  underlying: string;
  market: EvaluateBody["market"];
  dates: string[];
  prices?: number[];
  price_range_pct?: number;
  price_steps?: number;
}
export interface LegMeta {
  id: string;
  name: string;
  type: string;
}
export interface PayoffResponse {
  underlying: string;
  dates: string[];
  prices: number[];
  pnl: number[][];
  pnl_per_leg: Record<string, number[][]>;
  legs_meta: LegMeta[];
  shape: [number, number];
  break_even_at_last_date: number[];
}
export async function computePayoff(id: string, body: PayoffBody) {
  return request<PayoffResponse>(`/portfolios/${id}/payoff`, {
    method: "POST", body: JSON.stringify(body),
  });
}

// ---- Market data (autofill) ----
export interface QuoteResponse {
  symbol: string;
  last_price: number;
  currency: string;
  quality_marker: string;
  source: string;
  delay_minutes: number;
  bid: number | null;
  ask: number | null;
  cached?: boolean;
  cache_age_s?: number;
  stale?: boolean;
  data_quality?: string;
}
export async function fetchQuote(symbol: string) {
  return request<QuoteResponse>(`/market/quote?symbol=${encodeURIComponent(symbol)}`);
}

export interface SuggestedLeg {
  side: string;
  strike: number;
  premium: number;
  iv: number | null;
  bid: number | null;
  ask: number | null;
  open_interest: number | null;
  spread_pct: number | null;
}
export interface ChainResponse {
  underlying_symbol: string;
  underlying_price: number;
  expiry: string;
  dte: number;
  quality_marker: string;
  source: string;
  pct_otm_requested: number;
  atm_iv: number | null;
  iv_rank_pct: number | null;
  suggested: { call: SuggestedLeg | null; put: SuggestedLeg | null };
  n_call_strikes: number;
  n_put_strikes: number;
  cached?: boolean;
  cache_age_s?: number;
  stale?: boolean;
  data_quality?: string;
}
export async function fetchChain(symbol: string, dte = 365, pctOtm = 3) {
  return request<ChainResponse>(`/market/chain?symbol=${encodeURIComponent(symbol)}&dte=${dte}&pct_otm=${pctOtm}`);
}

export interface IvRankResponse {
  symbol: string;
  iv_rank_pct: number | null;
  current_iv?: number;
  note?: string;
}
export async function fetchIvRank(symbol: string) {
  return request<IvRankResponse>(`/market/iv-rank?symbol=${encodeURIComponent(symbol)}`);
}

// ---- Real expiration model (spec Market Data Integration) ----
export interface ExpirationsResponse {
  symbol: string;
  expirations_available: string[];
  best_expiration: string | null;
  best_expiration_days: number | null;
  best_expiration_status: "OK" | "WARN_OUT_OF_WINDOW" | "NO_SUITABLE_EXPIRATION";
  alternatives: { date: string; days: number }[];
  exit_point?: { exit_target: string; exit_window_start: string; exit_window_end: string; days_to_hold: number };
  stale?: boolean;
  cache_age_s?: number;
}
export async function fetchExpirations(symbol: string) {
  return request<ExpirationsResponse>(`/market/expirations?symbol=${encodeURIComponent(symbol)}`);
}

export interface IvHistoryLatest {
  last_date: string | null;
  last_phase: string | null;
  recorded_at: string | null;
  symbols_in_last: number;
  total_rows: number;
}
export async function fetchIvHistoryLatest() {
  return request<IvHistoryLatest>(`/market/iv-history-latest`);
}

export interface Greeks { delta: number; theta: number; vega: number; gamma: number; }
export interface StrangleAnalysis {
  symbol: string;
  spot: number;
  expiration: string;
  days_to_expiration: number;
  expiration_status: string;
  exit_point: { exit_target: string; exit_window_start: string; exit_window_end: string; days_to_hold: number };
  call: { strike: number; premium_now: number; premium_at_exit: number; iv: number; greeks?: Greeks };
  put: { strike: number; premium_now: number; premium_at_exit: number; iv: number; greeks?: Greeks };
  lot_multiplier: number;
  coverage: {
    premium_full: number; premium_at_exit_full: number; delta_to_exit: number;
    deposit_income: number; is_covered: boolean; surplus: number; days_held: number;
  };
  iv_rank: { iv_rank_pct: number | null; status: string; method: string; current_iv?: number; hv30?: number; vrp?: number };
  data_source?: string;   // tradier | yfinance | yfinance_fallback
  hv_2y?: number | null;  // annualized реализованная вола за 2г (доля, напр. 0.32) или null
  hv_10y?: number | null; // за 10л или null (молодые ETF)
  // Реальный годовой размах цены (ход за год от входа, доли): up_typ/dn_typ/best_typ — медианы,
  // up_last/dn_last — последний год. Не путать с HV (усреднённая σ).
  annual_range?: { up_typ: number; dn_typ: number; best_typ: number; up_last: number; dn_last: number; n_windows: number } | null;
  liquidity_flags: string[];
  pricing_note: string;
}
export interface StrangleAnalysisBody {
  symbol: string;
  lot_multiplier?: number;
  capital?: number;
  rate?: number;
  call_strike?: number | null;
  put_strike?: number | null;
  expiration?: string | null;
  pct_otm?: number;
}
export async function analyzeStrangle(body: StrangleAnalysisBody) {
  return request<StrangleAnalysis>(`/market/strangle-analysis`, { method: "POST", body: JSON.stringify(body) });
}

// ---- Screening (watchlist batch check) ----
export interface ScreeningOptionRow {
  strike: number;
  expiry: string;
  bid?: number | null;
  ask?: number | null;
  last?: number | null;
  quantity?: number;
  contract_multiplier?: number;
  premium?: number | null;
}
export interface ScreeningScenario {
  ticker: string;
  spot: number;
  capital: number;
  call?: ScreeningOptionRow | null;
  put?: ScreeningOptionRow | null;
  iv_rank_pct?: number | null;
  conservative_rate?: number;
  horizon_days?: number;
  note?: string | null;
}
export interface ScreeningGateOut {
  passed: boolean;
  skipped: boolean;
  rationale: string;
}
export interface ScreeningResult {
  ticker: string;
  accepted: boolean;
  gates: Record<string, ScreeningGateOut>;
  rejection_reasons: string[];
  total_premium: number;
  premium_pct_of_capital: number;
  dte: number | null;
  used_premium_call: number | null;
  used_premium_put: number | null;
  note: string | null;
}
export async function runScreening(scenarios: ScreeningScenario[], basis: "ask" | "mid" | "last" | "bid" = "ask") {
  return request<{ results: ScreeningResult[] }>("/screening/check", {
    method: "POST",
    body: JSON.stringify({ scenarios, premium_basis: basis }),
  });
}

// ---- Tracked symbols (серверный вотчлист; история IV не зависит от видимости) ----
export interface TrackedSymbolOut {
  symbol: string;
  data_symbol?: string | null;
  name?: string | null;
  cls?: string | null;
  visible: boolean;
  track_iv: boolean;
  priority?: "red" | "yellow" | "green" | null;
  trigger?: string | null;
  signal_date?: string | null;
  source_note?: string | null;
}
export async function fetchTrackedSymbols(visibleOnly = true) {
  return request<TrackedSymbolOut[]>(`/market/tracked-symbols?visible_only=${visibleOnly}`);
}
export async function addTrackedSymbol(body: { symbol: string; data_symbol?: string; name?: string; cls?: string }) {
  return request<TrackedSymbolOut>("/market/tracked-symbols", { method: "POST", body: JSON.stringify(body) });
}
/** Скрыть из UI. История IV и накопление кроном сохраняются (track_iv не трогается). */
export async function hideTrackedSymbol(symbol: string) {
  return request<null>(`/market/tracked-symbols?symbol=${encodeURIComponent(symbol)}`, { method: "DELETE" });
}

// ---- Price history (упрощённый график цены в Аналитике) ----
export interface PriceHistoryOut {
  symbol: string;
  period: string;
  points: [string, number][];
  n_raw: number;
  high: { date: string; price: number };
  low: { date: string; price: number };
  last: { date: string; price: number };
  stale?: boolean;
}
export async function fetchPriceHistory(symbol: string, period: "max" | "5y" | "1y" | "1mo" = "5y") {
  return request<PriceHistoryOut>(`/market/price-history?symbol=${encodeURIComponent(symbol)}&period=${period}`);
}
